// Who has to show their face before clocking in — asked once, for the whole app.
//
// This used to live as six pieces of local state inside the Attendance page,
// which was fine while that page was the only thing gating a punch. It is not
// any more: the sidebar clock button gates on exactly the same answer, and two
// independent copies of a security decision are two copies that can disagree —
// including two copies of the `everEntitled` latch below, whose whole job is to
// remember something across a failure.
//
// hrms owns every part of this decision. `/attendance/face/status` reports the
// deployment flag AND this user's Access Management grant, and is the ONLY
// thing the UI trusts: messenger's own useAccess() is fail-OPEN (a user with no
// access profile sees every panel), which is the exact inverse of the
// default-denied rule hrms applies to biometrics.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { getFaceStatus, type FaceStatus } from '../hrms/api/face';

export type FaceProbe = 'loading' | 'ok' | 'error';

export interface FaceCheckGate {
  /** null = not asked yet. Clock In must behave exactly as it always did until
   *  hrms has answered: a slow or missing face-service never delays a punch. */
  status: FaceStatus | null;
  /** Separate from `status` being null, which cannot tell "not asked yet" from
   *  "asked and failed" — and those two must not behave the same. */
  probe: FaceProbe;
  /** Who this person is: the deployment flag and their Access grant. */
  entitled: boolean;
  /** Entitled AND we can act on it right now. Deliberately separate from
   *  `entitled` — folding `available` into one flag made an unreachable
   *  side-car look identical to "this user is exempt", so Clock In quietly
   *  wrote an unverified punch and said nothing. */
  ready: boolean;
  /** Entitled, but the check cannot run. Callers must REFUSE the punch, not
   *  fall through to an ordinary one. */
  blocked: boolean;
  refresh: () => Promise<void>;
}

const FaceCheckGateContext = createContext<FaceCheckGate | null>(null);

export function FaceCheckGateProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Admins are exempt. Note this is the narrow check the Attendance page has
  // always used, NOT isAdminRole() — that one is manager-tier-or-above, and
  // using it here would silently exempt every manager from the face check.
  const exempt = (user?.role || '').toLowerCase() === 'admin';

  const [status, setStatus] = useState<FaceStatus | null>(null);
  const [probe, setProbe] = useState<FaceProbe>('loading');

  const refresh = useCallback(async () => {
    if (exempt) return;
    try {
      setStatus(await getFaceStatus());
      setProbe('ok');
    } catch {
      setStatus(null);
      setProbe('error');
    }
  }, [exempt]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const entitled = Boolean(status?.enabled && status.allowed);
  const ready = entitled && Boolean(status?.available);

  // Latches on first sight. A later failed probe must not clear it: "we could
  // not ask" is not "they are exempt", and reading it as exempt reopens the
  // exact downgrade this gate exists to close.
  const everEntitled = useRef(false);
  useEffect(() => {
    if (entitled) everEntitled.current = true;
  }, [entitled]);

  const blocked =
    (entitled && !status?.available)
    || (probe === 'error' && everEntitled.current);

  const value = useMemo<FaceCheckGate>(
    () => ({ status, probe, entitled, ready, blocked, refresh }),
    [status, probe, entitled, ready, blocked, refresh],
  );

  return (
    <FaceCheckGateContext.Provider value={value}>
      {children}
    </FaceCheckGateContext.Provider>
  );
}

export function useFaceCheckGate(): FaceCheckGate {
  const gate = useContext(FaceCheckGateContext);
  if (!gate) {
    throw new Error('useFaceCheckGate must be used within FaceCheckGateProvider');
  }
  return gate;
}
